import { useSelector } from "react-redux";
import { activeEnterprisePlanSelector } from "../selectors/pricings";
import { isV4PlanId, resolvePlanTier } from "../constants/pricingScreen";

/**
 * Map any (possibly legacy / V2/V3/V4) plan id to plan-type flags used for
 * feature gating. Uses resolvePlanTier so free↔freeV2 / growth↔growthV2 share
 * the same gates without pointing fee lookups at the wrong plan doc.
 */
export const getPlanTypes = (planId) => {
  let isOnStarterPlan = false,
    isOnProPlan = false,
    isOnBusinessPlan = false,
    isNewPlan = false;

  const tier = resolvePlanTier(planId);

  // Franchise plans (Big Frog HQ + locations) share the "MyCart only" channel
  // restriction with Free, but unlike Free they are full production houses, so
  // callers can use this flag to keep Manufacturing/Inventory available.
  const isFranchisePlan =
    tier === "franchise" || tier === "franchiseLocation";

  if (tier === "business" || tier === "enterprise") {
    isOnBusinessPlan = true;
    isNewPlan = true;
  } else if (tier === "growth") {
    isOnProPlan = true;
    isNewPlan = true;
  } else if (
    tier === "free" ||
    tier === "franchise" ||
    tier === "franchiseLocation"
  ) {
    // Franchise stays MyCart-only for sales channels. Free production-shop
    // founders still connect Shopify/Woo/etc via Riverr's shared apps
    // (`areThirdPartyStoresVisible`); this flag is the starter-tier marker.
    isOnStarterPlan = true;
    isNewPlan = true;
  }

  const isV4Plan = isV4PlanId(planId);
  // V4-only feature tightening. Pre-V4 / plan-less tenants keep prior access
  // (Inventory still follows Manufacturing visibility in mainNav — Bespoke and
  // other plan-less production shops must not lose the Inventory tab).
  const hasAdvancedInventory = isV4Plan
    ? isOnBusinessPlan || isFranchisePlan
    : true;
  const hasNexusAccess = isV4Plan
    ? isOnBusinessPlan || isFranchisePlan
    : true;
  const hasApiKeysAccess = isV4Plan
    ? isOnBusinessPlan || isFranchisePlan
    : true;

  return {
    isOnStarterPlan,
    isOnProPlan,
    isOnBusinessPlan,
    isNewPlan,
    isFranchisePlan,
    isV4Plan,
    hasAdvancedInventory,
    hasNexusAccess,
    hasApiKeysAccess,
  };
};

const RESERVED_MYRIVERR_HOSTS = new Set([
  "printify.myriverr.com",
  "bigfrog.myriverr.com",
  "locationstest.myriverr.com",
  "repyourbrand.myriverr.com",
]);

const normalizeHost = (host) =>
  String(host || "")
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split("/")[0]
    .trim();

export const isRiverrOwnedHost = (host) => {
  const h = normalizeHost(host);
  if (!h) return false;
  if (RESERVED_MYRIVERR_HOSTS.has(h) || h.startsWith("bigfrog-")) return false;
  return (
    h === "app.riverr.app" ||
    h === "accounts.riverr.app" ||
    h.endsWith(".myriverr.com")
  );
};

/**
 * Self-serve `{tenant}.myriverr.com` shops (Snap Express, etc.).
 * Any custom brand domain — app.rakiline.com, app.think.pro — is a
 * white-label enterprise and must not inherit Riverr's Shopify/Woo apps.
 */
export const isSelfServeRiverrTenant = ({
  hostnames = [],
  hostname = "",
  parentEnterpriseId = null,
} = {}) => {
  if (parentEnterpriseId) return false;
  const enterpriseHosts = (hostnames || []).map(normalizeHost).filter(Boolean);
  if (enterpriseHosts.length) {
    return enterpriseHosts.every(isRiverrOwnedHost);
  }
  return isRiverrOwnedHost(hostname);
};

/**
 * Third-party channel cards on /add-shop (Shopify, Woo, Etsy, …).
 * Franchise locations stay MyCart-only. Self-serve myriverr founders
 * see Riverr's shared integrations on Free. White-label enterprises
 * keep the original Growth/Business gate and never get Riverr cards
 * just because they are on Free.
 */
export const areThirdPartyStoresVisible = ({
  planId,
  isEnterpriseAdmin = false,
  isSelfServeRiverrTenant: selfServe = false,
} = {}) => {
  const { isNewPlan, isOnProPlan, isOnBusinessPlan, isFranchisePlan } =
    getPlanTypes(planId);
  if (isFranchisePlan) return false;
  if (!isNewPlan) return true;
  if (selfServe) {
    return !!isEnterpriseAdmin || isOnBusinessPlan;
  }
  return (
    (isEnterpriseAdmin && (isOnProPlan || isOnBusinessPlan)) ||
    (!isEnterpriseAdmin && isOnBusinessPlan)
  );
};

export function useGetUserPlanTypes() {
  const enterprisePlanId = useSelector(
    (state) =>
      state?.currentUser?.enterprise?.planId ||
      activeEnterprisePlanSelector(state)?.id
  );
  return getPlanTypes(enterprisePlanId);
}
